import {
  FormDraftConfig,
  FormField,
  FormFieldType,
  readFormConfig,
} from './formConfig';

export const RECURSIVE_FORM_LIMITS = {
  depth: 5,
  fieldNodes: 40,
  formNodes: 256,
  fields: 100,
  options: 100,
  configBytes: 256 * 1024,
  arrayItems: 100,
  answerNodes: 2000,
} as const;

/** Structural shape for the typed answer-tree renderer, not a config version. */
export type RecursiveFormNode = Omit<
  FormField,
  'fieldType' | 'properties' | 'items' | 'children'
> & { children?: RecursiveFormNode[] } & (
    | { fieldType: Exclude<FormFieldType, 'object' | 'array'> }
    | { fieldType: 'object'; properties: RecursiveFormNode[] }
    | { fieldType: 'array'; items: RecursiveFormNode }
  );

export interface RecursiveFormConfig extends Omit<FormDraftConfig, 'form'> {
  form: {
    id: string;
    type: 'section';
    label: string;
    children: RecursiveFormNode[];
  }[];
}

/** The canonical reader validates structural properties/items before narrowing. */
export function readRecursiveFormConfig(
  value: unknown
): RecursiveFormConfig | undefined {
  return readFormConfig(1, value) as RecursiveFormConfig | undefined;
}

/** Start containers with an editable text child; authors can change its type.
 * Structural children belong to their root answer and have no independent export settings.
 */
export function createRecursiveFormNode(
  fieldType: FormFieldType = 'shortText',
  structural = false
): RecursiveFormNode {
  const common = {
    id: crypto.randomUUID(),
    type: 'field' as const,
    label: '',
    extraction: { prompt: '' },
    ...(!structural
      ? {
          export: {
            includeReasoning: true,
            includeQuotes: true,
            includeCitations: true,
          },
        }
      : {}),
  };
  if (fieldType === 'object') {
    return {
      ...common,
      fieldType,
      properties: [createRecursiveFormNode('shortText', true)],
    };
  }
  if (fieldType === 'array') {
    return {
      ...common,
      fieldType,
      items: createRecursiveFormNode('shortText', true),
    };
  }
  if (fieldType === 'singleSelect' || fieldType === 'multiSelect') {
    return {
      ...common,
      fieldType,
      options: [{ label: 'Option 1', value: 'option-1' }],
    };
  }
  return { ...common, fieldType };
}
